// @flow
/* global Node */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import pubSub from 'app/common/lib/PubSub';
import type { HocClass0 } from 'app/common/lib/react/hocTypes';

export type BoundingBox = {
  bottom: number,
  height: number,
  left: number,
  right: number,
  top: number,
  width: number,
};

export type IntersectionChange = {
  boundingClientRect?: BoundingBox,
  intersectionRatio: number,
  isIntersecting: boolean,
  intersectionRect?: BoundingBox,
  rootBounds?: BoundingBox,
  target: any,
  time?: number,
};

const observeMap: Map<Node, (IntersectionChange) => void> = new Map();

let observer: any = null;

const onIntersect = (changes) => {
  changes.forEach((change: IntersectionChange) => {
    const callback = observeMap.get(change.target);
    if (callback) {
      callback(change);
    }
  });
};

const unobserve = (node: Node) => {
  observer.unobserve(node);
  observeMap.delete(node);
};

const observe = (
  node: Node,
  inAdsDesktopVideoExperiment?: boolean,
  callback: (IntersectionChange) => void,
) => {
  // if in ads video experiment, add additional 50% trigger
  const threshold = inAdsDesktopVideoExperiment ? [0, 0.5, 1] : [0, 1];
  const options = {
    root: document.querySelector('#mainContainer'),
    rootMargin: '-64px 0px 0px 0px', // Needed to make sure area behind the header is not counted as visible
    threshold,
  };
  observer = observer || new window.IntersectionObserver(onIntersect, options);
  observeMap.set(node, callback);
  observer.observe(node);
};

const isObserving = (node: Node) => observeMap.has(node);

/*
 * withVisibilityChanged: (React Component) => React Component (with onVisibilityChanged prop)
 *
 * Input: Given visibility changed args and a react component
 * Output: a react component with an onVisibilityChanged prop that fires whenever component entered or exited
 */

export type VisibilityChangedProp = {
  /*
   * Called whenever this is entered or exitted
   * visible is false if exited, true if entered
   */
  inAdsDesktopVideoExperiment?: boolean,
  onVisibilityChanged: (visible: boolean) => void,
  trackFullVisible?: boolean,
};

export type WithVisibilityChanged<Props, Context> = HocClass0<
  Props,
  Props & VisibilityChangedProp,
  Context,
>;

const withVisibilityChanged: WithVisibilityChanged<any, any> = (Subject) => {
  class IntersectionObserver extends Component<VisibilityChangedProp> {
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    static displayName = `WithVisibilityChanged(${Subject.displayName || Subject.name})`;
    componentDidMount() {
      this.observe();
    }

    componentWillUnmount() {
      const node = this._node;
      if (node) {
        this.leave();
        unobserve(node);
      }
    }

    _node: ?Object; // findDOMNode returns `any`
    seen: boolean = false;
    denzelNavigateListener: any;
    flushTimeout: ?number = null;

    attachDenzelNavigateListener = () => {
      this.denzelNavigateListener = pubSub.instance.subscribe(
        PCONST.PUBSUB_CHANNEL_SITE,
        PCONST.PUBSUB_TOPIC_NAVIGATE_STARTED,
        (_, __, message, addingSubscriber) => {
          const closeupUrl = new RegExp('/pin/[\\w|-]+/$');
          if (
            !addingSubscriber &&
            message &&
            typeof message.url === 'string' &&
            closeupUrl.test(message.url)
          ) {
            this.leave();
          }
        },
      );
    };

    detachDenzelNavigateListener = () => {
      if (this.denzelNavigateListener) {
        this.denzelNavigateListener.unsubscribe();
        delete this.denzelNavigateListener;
      }
    };

    enter = (change) => {
      // there are cases where the change is reported as 0 but the element is in the viewport, listen for either event
      const inViewport = change.intersectionRatio > 0 || change.isIntersecting;
      this.seen = inViewport;
      if (this.seen) {
        this.props.onVisibilityChanged(true);
        this.attachDenzelNavigateListener();
      }
    };

    leave = () => {
      const node = this._node;
      if (!node || !isObserving(node)) {
        return;
      }
      if (this.seen) {
        this.props.onVisibilityChanged(false);
        this.seen = false;
        this.detachDenzelNavigateListener();
      }
    };

    observe = () => {
      try {
        this._node = ReactDOM.findDOMNode(this);
      } catch (e) {
        return;
      }
      if (!this._node) {
        return;
      }

      const { inAdsDesktopVideoExperiment } = this.props;

      const node = this._node;
      observe(node, inAdsDesktopVideoExperiment, (change) => {
        if (!observeMap.has(node)) {
          return;
        }

        // if in ads video experiment, trigger autoplay start/stop at 50% view
        const nonfullVisibleIntersection = inAdsDesktopVideoExperiment
          ? change.intersectionRatio >= 0.5
          : change.intersectionRatio > 0 || change.isIntersecting;

        // if the component has to be fully visible wait for an intersection ratio of 1,
        // if not there are cases where the element can be in viewport and have a ratio of 0, listen for either
        const inViewport = this.props.trackFullVisible
          ? change.intersectionRatio >= 1
          : nonfullVisibleIntersection;

        const outViewport = this.props.trackFullVisible
          ? change.intersectionRatio === 0
          : !inViewport;
        if (!this.seen && inViewport) {
          this.enter(change);
        } else if (this.seen && outViewport) {
          this.leave();
        }
      });
    };

    render() {
      return <Subject {...this.props} />;
    }
  }
  return IntersectionObserver;
};

export default withVisibilityChanged;
